import React, { useEffect, useState, useRef } from 'react';
import { Modals } from '../../Consts';
import { useAppContext } from '../../AppContext';
import { Button, FormItem, FormLayoutGroup, ModalCard, Caption } from '@vkontakte/vkui';
import NameInputComponent from './components/NameInputComponent';
import { clearCache as eventsListCacheClear } from './EventsListPanel';
import { ErrorLoadComponent } from '../components/ErrorLoadComponent';
import { getResponseFromError, prepareString } from '../../helpers/helpers';
import { UploadImageComponent } from './components/UploadImageComponent';
import { getRouteEventInfo } from '../../route';

const CURRENT_MODAL = Modals.CREATE_EVENT_MODAL;

const CreateEventModal = () => {
  const context = useAppContext();

  const [name, setName] = useState('');
  const [nameError, setNameError] = useState('');
  const [image, setImage] = useState(null);
  const [imageError, setImageError] = useState('');
  const [sendSave, setSendSave] = useState(false);
  const [error, setError] = useState(null);
  const isFormSavedRef = useRef(false);

  const fetch = () => {
    context.spinner(true);
    context.init()
      .then(() => {
        context.spinner(false);
        setError(null);
      })
      .catch((e) => {
        setError(e);
        context.showError(e);
      });
  }

  useEffect(() => {
    fetch();
  }, []);

  const createEvent = (name, image, isGenerateImage) => {
    if (isFormSavedRef.current) {
      return;
    }

    isFormSavedRef.current = true;
    setSendSave(true);
    context.spinner(true);
    const touchId = (Math.random() + 1).toString(36).substring(2);
    context.httpClient()
      .addEventV4(prepareString(name), image?.id, isGenerateImage, touchId)
      .then((response) => {
        eventsListCacheClear(context.cache);
        setSendSave(false);
        isFormSavedRef.current = false;
        context.spinner(false);
        context.store.setIsShowConfirmShareLink(true);
        context.router.replace(...getRouteEventInfo(response.data.event.id));
      })
      .catch((error) => {
        setSendSave(false);
        isFormSavedRef.current = false;
        const response = getResponseFromError(error);
        if (!response) {
          context.showError(error);
          return;
        }

        setNameError(response.name || '');
        delete response['name'];
        setImageError(response.image || '');
        delete response['image'];

        context.spinner(false);

        if (response.message) {
          context.showError(null, response.message);
        } else if (Object.keys(response).length > 0) {
          context.showError(error);
        }
      });

    context.getBridge().retargeting.createEvent(context.store.currentUserId);
  }

  const onSave = () => {
    if (context.store.generateEventImageEnabled) {
      context.confirm(
        context.lang().getKey(CURRENT_MODAL, 'generate_image_confirm_text'),
        (value) => {
          createEvent(name, image, value);
        },
        context.lang().getKey(CURRENT_MODAL, 'generate_image_ok_label'),
        context.lang().getKey(CURRENT_MODAL, 'generate_image_cancel_label')
      );
    } else {
      createEvent(name, image, false);
    }
  }

  if (error) {
    return (<ErrorLoadComponent
      error={error}
      onFetch={fetch}
    />)
  }

  return (
    <ModalCard
      id={CURRENT_MODAL}
      onClose={() => context.back()}
      header={context.lang().getKey(CURRENT_MODAL, 'header')}
    >
      {context.snackbar}
      <FormLayoutGroup mode="vertical">
        <NameInputComponent
          value={name}
          setValue={setName}
          error={nameError}
          setError={setNameError}
          label={context.lang().getKey('app', 'name_input_label')}
          errorMinLength={context.lang().getKey('errors', 'name_error_min_length_3_char')}
        />
        <UploadImageComponent
          value={image}
          setValue={setImage}
          error={imageError}
          setError={setImageError}
          label={context.lang().getKey('app', 'image_input_label')}
          buttonLabel={context.lang().getKey('app', 'image_button_label__empty')}
          maxSizeError={context.lang().getKey('errors', 'image_error_max_size')}
          errorTextType={context.lang().getKey('errors', 'image_error_type')}
          maxSize={context.store.maxFileSize}
        />
        <FormItem
          bottom={<Caption level={'3'}>{context.lang().getKey(CURRENT_MODAL, 'create_count_limit')}</Caption>}
        >
          <Button
            size="l"
            stretched={true}
            loading={sendSave}
            disabled={sendSave}
            onClick={() => onSave()}
          >
            {context.lang().getKey(CURRENT_MODAL, 'save_button')}
          </Button>
        </FormItem>
      </FormLayoutGroup>
    </ModalCard>
  );
}

export default CreateEventModal;